/** Shared check-in type label helpers for PA queue + processing. */

export const CHECKIN_TYPE_MAP = {
  1: 'In-Patient',
  2: 'Out-Patient',
  3: 'Antenatal',
  4: 'Dental',
  5: 'Optical',
  in: 'In-Patient',
  out: 'Out-Patient',
  'in-patient': 'In-Patient',
  'out-patient': 'Out-Patient',
};

/**
 * Resolve a human-readable check-in type from common PA payload shapes.
 * @param {Record<string, unknown>|null|undefined} record
 * @returns {string|null}
 */
export function resolveCheckinTypeLabel(record) {
  if (!record || typeof record !== 'object') return null;

  const namedCandidates = [
    record.checkin_type_name,
    record.checkinName,
    record.checkin_type,
    record.checkinTypeName,
    record.checkin_type_label,
  ];

  for (const named of namedCandidates) {
    if (named == null) continue;
    const text = String(named).trim();
    if (!text) continue;
    if (text.toLowerCase() === 'unknown') continue;
    // If API already returned a friendly name, keep it.
    if (Number.isNaN(Number(text))) return text.__capitalize?.() || text;
    // Numeric string masquerading as a name — fall through to map.
    if (CHECKIN_TYPE_MAP[text] != null) return CHECKIN_TYPE_MAP[text];
    if (CHECKIN_TYPE_MAP[Number(text)] != null) return CHECKIN_TYPE_MAP[Number(text)];
  }

  const raw = record.checkinType ?? record.checkin_type_id ?? null;
  if (raw == null || raw === '') return null;

  if (CHECKIN_TYPE_MAP[raw] != null) return CHECKIN_TYPE_MAP[raw];
  const asNum = Number(raw);
  if (!Number.isNaN(asNum) && CHECKIN_TYPE_MAP[asNum] != null) return CHECKIN_TYPE_MAP[asNum];

  const asText = String(raw).trim();
  if (!asText) return null;
  if (CHECKIN_TYPE_MAP[asText.toLowerCase()] != null) return CHECKIN_TYPE_MAP[asText.toLowerCase()];
  if (Number.isNaN(Number(asText))) return asText.__capitalize?.() || asText;
  return null;
}

/**
 * Ensure queue/list rows always expose checkin_type_name for the UI.
 * @param {Record<string, unknown>} claim
 */
export function withResolvedCheckinType(claim) {
  if (!claim || typeof claim !== 'object') return claim;
  const label = resolveCheckinTypeLabel(claim);
  if (!label) return claim;
  return {
    ...claim,
    checkin_type_name: label,
    checkinName: claim.checkinName || label,
  };
}
